import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Loading } from 'react-admin';
import { RequireAccess } from '../auth/RequireAccess';
import { ReferralsBand } from './ReferralsBand';
import { useReferrals } from './useReferrals';

/**
 * Referrals explorer - the third Phase 4 surface, READ-MOSTLY (a filtered view
 * over users, NOT a CRUD resource: an admin never creates a referral, users do by
 * referring each other). Subscriptions-shaped seam. One guarded write, Void, so
 * the audit floor is live for that action only.
 *
 * A referral is a RELATIONSHIP (referrer -> referee), stored as the one-hop
 * self-FK user.referredBy. The explorer is keyed by the REFERRER, banded by the
 * referrer's market (UK one band, US one band, GLOBAL two stacked), keyed off the
 * region scope not the switcher. A cross-market referee is flagged, never hidden.
 *
 * The referrer earns a reward only once the referee SPENDS a threshold (the
 * anti-abuse gate). Reward conferral is a future engine's job; the admin DISPLAYS
 * state and progress and can VOID, never pays (D-referrals).
 */

/** The surface without the access wrapper. Exported so tests can inject `asOf`
 *  (used for the "referred N days ago" display). */
export const ReferralsView = ({ asOf }: { asOf?: number }) => {
  // One reference clock for the relative referred-date display, captured once.
  const [now] = useState<number>(() => asOf ?? Date.now());

  const { view, isLoading } = useReferrals();

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: 1 }}
      >
        Members
      </Typography>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
        Referrals
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Who referred whom, keyed by referrer. A referrer earns a reward only
        once the referee spends the threshold, so a stalled pending referral
        with no spend is most likely an abandoned or fake one. The reward engine
        writes state; the admin can void a fraudulent referral, never pay.
      </Typography>

      {isLoading || !view ? (
        <Loading />
      ) : view.bands.length === 0 ? (
        <Typography color="text.secondary">
          No market is in scope for this account.
        </Typography>
      ) : (
        view.bands.map((band) => (
          <ReferralsBand key={band.market} band={band} now={now} />
        ))
      )}
    </Box>
  );
};

/**
 * Gated entry registered at /referrals. `referrals:read` is a read capability, so
 * viewer (read everywhere) and super_admin see it, and editor is granted it
 * explicitly (D-referrals); the placeholder support / fraud roles do not. The void
 * action gates itself on `referrals:void`.
 */
export const Referrals = () => (
  <RequireAccess capability="referrals:read">
    <ReferralsView />
  </RequireAccess>
);
